import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsivePie } from '@nivo/pie';
import { Fetch } from '../../utils/Utils.js';
import '../reproduction/NivoChartCard.css';
import './CategoricalDonut.css';

// Breed is identity, not status — fixed categorical order (palette.md slots
// 1-8), assigned by rank so color never depends on an interactive filter.
const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const MAX_SLICES = 8;

const CenterLabel = (totalCount, animalsWord) => ({ centerX, centerY }) => (
  <g>
    <text x={centerX} y={centerY - 6} textAnchor="middle" dominantBaseline="middle" className="hb-hero-value">
      {totalCount}
    </text>
    <text x={centerX} y={centerY + 15} textAnchor="middle" dominantBaseline="middle" className="hb-hero-label">
      {animalsWord}
    </text>
  </g>
);

const HerdByBreed = () => {
  const { t } = useTranslation();
  const [animals, setAnimals] = useState([]);

  useEffect(() => {
    Fetch('/api/herd/animals', 'GET', null, (data) => setAnimals(data || []));
  }, []);

  const animalsLabel = (n) => (n === 1 ? t('animal_count_singular') : t('animals_count_short'));

  const counts = {};
  animals.forEach((a) => {
    if (a.is_alive === false) return;
    const breed = a.breed || 'unknown';
    counts[breed] = (counts[breed] || 0) + 1;
  });

  const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const top = ranked.slice(0, MAX_SLICES);
  const rest = ranked.slice(MAX_SLICES);
  if (rest.length > 0) {
    const otherTotal = rest.reduce((sum, [, count]) => sum + count, 0);
    top.push(['other_breeds', otherTotal]);
  }

  const total = top.reduce((sum, [, count]) => sum + count, 0);
  const slices = top.map(([breed, count], i) => ({
    breed,
    count,
    percent: Math.round((count / total) * 100),
    color: SERIES_COLORS[i % SERIES_COLORS.length],
  }));

  const pieData = slices.map(s => ({ id: s.breed, label: t(s.breed), value: s.count, color: s.color }));

  const summary = slices.length > 0
    ? t('chart_summary_herd_by_breed', { percent: slices[0].percent, breed: t(slices[0].breed) })
    : '';

  return (
    <div className="Chart NivoCard HerdByBreed">
      <h3>{t('herd_by_breed')}</h3>
      {summary && <p className="nivo-card-subtitle">{summary}</p>}

      {total === 0 ? (
        <p className="nivo-card-empty">{t('chart_summary_no_herd_data')}</p>
      ) : (
        <div className="hb-body">
          <div className="hb-donut-wrap">
            <ResponsivePie
              data={pieData}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              innerRadius={0.62}
              padAngle={1.6}
              cornerRadius={4}
              activeOuterRadiusOffset={6}
              colors={{ datum: 'data.color' }}
              borderWidth={0}
              enableArcLinkLabels={false}
              arcLabelsSkipAngle={26}
              arcLabelsTextColor="#1a1714"
              arcLabel={d => `${Math.round((d.value / total) * 100)}%`}
              motionConfig="gentle"
              layers={['arcs', 'arcLabels', CenterLabel(total, animalsLabel(total))]}
              tooltip={({ datum }) => (
                <div className="nivo-tooltip">
                  <span className="nivo-tooltip-swatch" style={{ background: datum.data.color }} />
                  <strong>{datum.label}</strong>: {datum.value} ({Math.round((datum.value / total) * 100)}%)
                </div>
              )}
            />
          </div>

          <ul className="hb-legend">
            {slices.map(s => (
              <li key={s.breed} className="hb-legend-row" style={{ '--row-accent': s.color }}>
                <span className="hb-legend-dot" style={{ background: s.color }} />
                <span className="hb-legend-text">
                  <span className="hb-legend-headline">
                    <strong className="hb-legend-percent">{s.percent}%</strong>
                    {' '}{t(s.breed)}
                  </span>
                  <span className="hb-legend-count">{s.count} {animalsLabel(s.count)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HerdByBreed;
